import {
  AcquisitionChannel,
  AirbnbQualityStandardState,
  AirbnbTripIssuesOrderBy,
  ComparisonOperator,
  DataDistributionCategory,
  DayEntry,
  DistributionIssuePriority,
  DistributionWithIssueFilter,
  Granularity,
  GuestReadinessStatus,
  HomeByListingDataSorting,
  HomeConfigAuditStatus,
  HomeConfigRoomsAudit,
  HomeLinks,
  HomeQuerySorting,
  IDate,
  InHouseMonitoringStatus,
  LicensingFramework,
  ListingStatus,
  OnBoardingStage,
  OnBoardingStatus,
  OnlineTravelAgency,
  PetPolicyFieldKind,
  RatingSorting,
  RevenueModel,
  RiskLevel,
  SortOrder,
  TargetPartner,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { getHomesLocations, getHomesOTAStatusWithStats } from '@arriere/modules/propertiesList'
import { HomesDistributionForDistribution } from '@arriere/types/Properties.types'

export enum ViewModeTypes {
  AIRBNB_TRIP_ISSUES = 'airbnb-trip-issues',
  ASSETS_PERFORMANCE_DASHBOARD = 'assets_performance_dashboard',
  BULK_EDIT = 'bulk-edit',
  COLLECTIONS = 'collections',
  CONFIGURATION_STATUS = 'configuration_status',
  CONFIGURATION_STATUS_OTA = 'configuration_status_ota',
  DISTRIBUTION_CONNECTIONS = 'distribution-connections',
  DISTRIBUTION_LISTING_STATUS = 'distribution-listing-status',
  FINANCE = 'finance',
  LOCATIONS = 'locations',
  OCCUPANCY = 'occupancy',
  OPERATIONS = 'operations',
  PERFORMANCE = 'performance',
  RATINGS = 'properties', // We use `properties` because this view is rendered on the ratings route: '/ratings'
  REVENUE_CONFIGURATION = 'revenue_configuration',
  ACCOUNTING_RESOLUTION_CENTER = 'accounting_resolution_center',
  VENDOR_RATES = 'vendor_rates',
}

interface PaginationInput {
  page: number
  size: number
}

export interface SharedPropertiesParams {
  regionId?: UUID
  regionIds?: UUID[]
  name?: string
  onlyActive?: boolean
  minOccupancy?: number
  minPets?: number
  petPolicy?: PetPolicyFieldKind
  minBathrooms?: number
  minBedrooms?: number
  inHouseMonitoringStatuses?: InHouseMonitoringStatus[]
  filterableAmenityIds?: UUID[]
  updatedAfter?: IDate
  onlyWithDownListings?: boolean
  sort?: HomeQuerySorting
  riskLevel?: RiskLevel[]
  licensingFramework?: LicensingFramework[]
  targetPartner?: TargetPartner[]
  revenueModel?: RevenueModel[]
  pagination?: PaginationInput
  rooms?: number
  gallery?: number
  agency?: OnlineTravelAgency
  withOffboarded?: boolean
}

export interface PropertiesFinanceParams extends SharedPropertiesParams {
  from: IDate
  to: IDate
  fromPastYear: IDate
  toPastYear: IDate
  granularity: Granularity
  quantity?: number
  acquisitionChannel?: AcquisitionChannel[]
}

interface OtaFiltersProps {
  agency: OnlineTravelAgency
  listingStatus?: ListingStatus[]
  otaListingActive?: boolean
  otaListingName?: string
  priorities?: DistributionIssuePriority[]
  sort?: HomeByListingDataSorting
}

export interface PropertiesSyncStatusParams extends Omit<SharedPropertiesParams, 'agency'> {
  agency: OnlineTravelAgency
  currentOnly?: boolean
  issuesFilter?: DistributionWithIssueFilter
  otaFilters?: OtaFiltersProps
}

export interface DistributionListingsSyncStatusParams extends Omit<
  SharedPropertiesParams,
  'agency'
> {
  agency: OnlineTravelAgency
  otaFilters?: OtaFiltersProps
  listingKind?: string[]
}

export interface PropertiesDatesParams extends SharedPropertiesParams {
  from: IDate
  to: IDate
  granularity?: Granularity
  quantity?: number
}

export interface RatingSort {
  ratingSorting: RatingSorting
  asc: boolean
}

export interface RatingFilter {
  column: RatingSorting
  operator: ComparisonOperator
  value: number
}

export interface PropertiesRatingsParams extends Omit<SharedPropertiesParams, 'sort'> {
  from: IDate
  to: IDate
  granularity?: Granularity
  quantity?: number
  sort?: RatingSort[]
  filters?: RatingFilter[]
}

export interface PropertiesAirbnbTripIssuesParams {
  homeIds: string[]
  regionIds: string[]
  terms?: string
  listedOnly?: boolean
  qualityStandardState?: AirbnbQualityStandardState[]
  orderBy?: AirbnbTripIssuesOrderBy
  sortBy?: SortOrder
  pagination?: PaginationInput
}

export interface MarketsAirbnbTripIssuesParams {
  pagination: PaginationInput
}

export interface Indicator {
  aggregatedValue: number
}

export interface FinanceIndicators {
  bookingVolume?: Indicator
  realizedRevenue?: Indicator
  revpau?: {
    period?: number
    weekdays?: number
    weekends?: number
  }
  adr?: {
    period?: number
  }
  pastRealizedRevenue?: Indicator
}

export interface SalesIndicators {
  occupancyRate?: Indicator
  weekendOccupancyRate?: Indicator
}

export interface NpsInfoIndicators {
  count?: number
  conversionRate?: number
  numberOfBookings?: number
  score?: number
}

export interface CustomerExperienceIndicators {
  painBookings?: Indicator
  nps?: number
  npsInfo?: NpsInfoIndicators
}

export interface HomeIndicators {
  finance?: FinanceIndicators
  sales?: SalesIndicators
  customerExperience?: CustomerExperienceIndicators
}

export interface HomeDetails {
  onBoardingStage?: OnBoardingStage
  liveSince?: IDate
  onBoardingStatus?: OnBoardingStatus
}

export interface Rating {
  avgStars: number
  totalRates?: number
}

export interface AirBnbColumnRatings {
  overall?: Rating
  accuracy?: Rating
  checkIn?: Rating
  communication?: Rating
  cleanliness?: Rating
  location?: Rating
  value?: Rating
}

export interface AirBnbRatings extends AirBnbColumnRatings {
  conversionRate?: number
  numberOfBookings?: number
  numberOfReviews?: number
}

export interface HomeRatings {
  airBnb: AirBnbRatings
  homeAway: {
    overall: Rating
    conversionRate?: number
    numberOfBookings?: number
    numberOfReviews?: number
  }
}

export interface HomeDistributionIssue {
  reason: string
  internalCategory: DataDistributionCategory
  ts?: IDate
  code?: string
  issuePriority?: DistributionIssuePriority
}

export interface ListingChannel {
  active?: boolean
  listingId?: UUID
  listingLink?: string
  ota?: OnlineTravelAgency
}

export interface HomeListing {
  active?: boolean
  apiConnected?: boolean
  agencyAccount?: {
    agency?: string
    channel?: string
    channelAccountId?: string
    id?: string
    market?: string
  }
  channels?: ListingChannel[]
  externalHomeId?: string
  title?: string
}

export type HomeDistributions = HomesDistributionForDistribution

export interface LiveOccupancy {
  devices: number
  threshold: number
  underAlert: boolean
}

export interface ConfigurationStatus {
  strPermit: boolean
  galleryPicturesCount: number
  lockType?: string
  fallbackCode: HomeConfigAuditStatus
  accessInstructions: HomeConfigAuditStatus
  manualSections: number
  rooms: HomeConfigRoomsAudit
  pricing: HomeConfigAuditStatus
}

export interface CustomFieldsField {
  id: UUID
  name: string
  value?: string
}

export interface CustomFields {
  fields: CustomFieldsField[]
}

export interface DistributionChannelsWithIssuesProps {
  distributionChannelWithErrors: string[]
  distributionChannelWithWarnings: string[]
}

export interface HomeProps {
  id: string
  name: string
  maxOccupancy?: number
  hotelId?: string
  latitude?: number
  longitude?: number
  active?: boolean
  details?: HomeDetails
  lifecycle?: {
    riskLevel?: RiskLevel
    listingStatus?: ListingStatus
    guestReadinessStatus?: GuestReadinessStatus
  }
  thumbnail?: {
    url: string
  }
  links?: HomeLinks
  createdAt?: IDate
  region?: { name: string; id: string }
  petPolicy?: PetPolicyFieldKind
  activeLeaseMonthlyAmount?: number
  revenueModel?: RevenueModel
  configurationStatus?: ConfigurationStatus
  distributions?: HomeDistributions[]
  listings?: HomeListing[]
  leases?: Array<{ startDate: IDate; endDate: IDate }>
  upcomingWeekendsRelativeOccupancy?: number
  indicators?: HomeIndicators
  inquiriesCount?: number
  upcomingWeekendsOccupancy?: DayEntry[][]
  roomsCount?: number
  bathroomsCount?: number
  liveOccupancy?: LiveOccupancy
  ratings?: HomeRatings
  customFields?: CustomFields[]
  distributionChannelsWithIssues?: DistributionChannelsWithIssuesProps
}

export type MapHome = Awaited<ReturnType<typeof getHomesLocations>>['results'][number]

export type HomeDistributionWithStatsView = Awaited<
  ReturnType<typeof getHomesOTAStatusWithStats>
>['results'][number]

export type HomeResults = HomeProps | MapHome | HomeDistributionWithStatsView
